<template>
  <div>
    <div class="pt-3 pb-4">
      <h3 class="text-2xl font-bold mb-5">
        {{ title || $t("add_exchange_account") }}
      </h3>

      <div
        v-html="desc"
        class="mt-2 mb-4 text-sm opacity-70 prose max-w-none"
        :class="[$theme.baseTextColor]"
      ></div>
      <template v-if="loading">
        <LoadingBlock />
      </template>
      <template v-else>
        <div v-if="activeTab === 'manual'">
          <Listbox as="div" v-model="selectedExchange">
            <ListboxLabel
              class="block text-sm font-medium leading-6"
              :class="[$theme.cardLabelTextColor]"
            >
              {{ $t("exchange") }}
            </ListboxLabel>
            <div class="relative mt-2">
              <ListboxButton
                class="block w-full rounded-full border-0 px-3 py-2 ring-0 ring-inset focus:ring-1 focus:ring-inset"
                :class="[
                  $theme.baseTextColor,
                  $theme.inputPlaceTextColor,
                  $theme.inputRingFocusColor,
                  $theme.inputBorderColor,
                  $theme.inputRingColor,
                  $theme.inputBg,
                ]"
              >
                <span class="flex items-center">
                  <template v-if="selectedExchange">
                    <img
                      :src="selectedExchange.icon ? selectedExchange.icon : `/img/${selectedExchange.slug}.png`"
                      alt=""
                      class="h-5 w-5 flex-shrink-0 rounded-full"
                    />
                    <span class="ml-3 block truncate">{{
                      selectedExchange.name
                    }}</span>
                  </template>
                  <template v-else>
                    <span class="ml-3 block truncate">{{
                      $t("select_exchange")
                    }}</span>
                  </template>
                </span>
                <span
                  class="pointer-events-none absolute inset-y-0 right-0 ml-3 flex items-center pr-2"
                >
                  <ChevronUpDownIcon
                    class="h-5 w-5"
                    :class="[$theme.cardLabelTextColor]"
                    aria-hidden="true"
                  />
                </span>
              </ListboxButton>

              <transition
                leave-active-class="transition ease-in duration-100"
                leave-from-class="opacity-100"
                leave-to-class="opacity-0"
              >
                <ListboxOptions
                  class="absolute z-10 mt-1 max-h-56 w-full overflow-auto rounded-xl py-1 text-base shadow-lg ring-1 ring-opacity-5 focus:outline-none"
                  :class="[
                    $theme.baseTextColor,
                    $theme.inputPlaceTextColor,
                    $theme.inputRingFocusColor,
                    $theme.inputBorderColor,
                    $theme.inputRingColor,
                    $theme.inputBg,
                  ]"
                >
                  <ListboxOption
                    as="template"
                    v-for="exchange in exchanges"
                    :key="exchange.id"
                    :value="exchange"
                    v-slot="{ active }"
                  >
                    <li
                      :class="[
                        active ? ' text-gray-900' : 'text-gray-900',
                        'relative cursor-default select-none py-2 pl-3 pr-9',
                        $theme.baseTextColor,
                        $theme.inputPlaceTextColor,
                        $theme.inputRingFocusColor,
                        $theme.inputBorderColor,
                        $theme.inputRingColor,
                      ]"
                    >
                      <div class="flex items-center">
                        <img
                          :src="exchange.icon ? exchange.icon : `/img/${exchange.slug}.png`"
                          alt=""
                          class="h-5 w-5 flex-shrink-0 rounded-full"
                        />
                        <span
                          :class="[
                            active ? 'font-semibold' : 'font-normal',
                            'ml-3 block truncate',
                          ]"
                        >
                          {{ exchange.name }}
                        </span>
                      </div>
                      <span
                        v-if="
                          selectedExchange &&
                          selectedExchange.slug === exchange.slug
                        "
                        :class="[
                          $theme.navActiveTextColor2,
                          'absolute inset-y-0 right-0 flex items-center pr-4',
                        ]"
                      >
                        <CheckIcon class="h-5 w-5" aria-hidden="true" />
                      </span>
                    </li>
                  </ListboxOption>
                </ListboxOptions>
              </transition>
            </div>
          </Listbox>

          <div class="mt-4">
            <div class="relative">
              <div
                class="rounded-md p-2 mb-2 relative hidden"
                :class="[$theme.infoAlertBg]"
              >
                <div class="flex">
                  <div class="flex-shrink-0">
                    <InformationCircleIcon
                      class="h-5 w-5"
                      :class="[$theme.infoAlertIconColor]"
                      aria-hidden="true"
                    />
                  </div>
                  <div class="ml-3 flex-1 md:flex md:justify-between">
                    <p class="text-sm" :class="[$theme.infoAlertTextColor]">
                      {{ $t("add_ip_addresses") }}
                    </p>
                  </div>
                </div>
              </div>
              <div v-if="selectedExchange" class="relative mb-3">
                <ListIps :list_ips="allowedIPs" :exchange_type="selectedExchange.slug" />
              </div>
            </div>
            <div class="mt-4">
              <label
                for="api_key"
                class="block text-sm font-medium leading-6"
                :class="[$theme.cardLabelTextColor]"
                >API Key</label
              >
              <div class="mt-2">
                <input
                  v-model="form.api_key"
                  type="text"
                  id="api_key"
                  class="block w-full rounded-full border-0 py-2 px-3 ring-0 ring-inset focus:ring-1 focus:ring-inset"
                  :class="[
                    $theme.baseTextColor,
                    $theme.inputPlaceTextColor,
                    $theme.inputRingFocusColor,
                    $theme.inputBorderColor,
                    $theme.inputRingColor,$theme.inputBg,
                  ]"
                />
              </div>
            </div>
            <div class="mt-4">
              <label
                for="api_secret"
                class="block text-sm font-medium leading-6"
                :class="[$theme.cardLabelTextColor]"
                >API Secret</label
              >
              <div class="mt-2">
                <input
                  v-model="form.api_secret"
                  type="text"
                  id="api_secret"
                  class="block w-full rounded-full border-0 py-2 px-3 ring-0 ring-inset focus:ring-1 focus:ring-inset"
                  :class="[
                    $theme.baseTextColor,
                    $theme.inputPlaceTextColor,
                    $theme.inputRingFocusColor,
                    $theme.inputBorderColor,
                    $theme.inputRingColor,$theme.inputBg,
                  ]"
                />
              </div>
            </div>
            <div class="mt-4">
              <label
                for="api_secret"
                class="block text-sm font-medium leading-6"
                :class="[$theme.cardLabelTextColor]"
              >
                {{ $t("api_passphrase") }}
              </label>
              <div class="mt-2">
                <input
                  v-model="form.api_passphrase"
                  type="text"
                  id="api_secret"
                  class="block w-full rounded-full border-0 py-2 px-3 ring-0 ring-inset focus:ring-1 focus:ring-inset"
                  :class="[
                    $theme.baseTextColor,
                    $theme.inputPlaceTextColor,
                    $theme.inputRingFocusColor,
                    $theme.inputBorderColor,
                    $theme.inputRingColor,$theme.inputBg,
                  ]"
                />
              </div>
            </div>
          </div>
          <template v-if="formError.errors !== null">
            <div class="rounded-xl border-red-600 border p-3 mt-4">
              <div class="flex">
                <div class="flex-shrink-0">
                  <XCircleIcon
                    class="h-5 w-5 text-red-700"
                    aria-hidden="true"
                  />
                </div>
                <div class="ml-2">
                  <h3 class="text-sm font-medium text-red-700">
                    {{ formError.message }}
                  </h3>
                  <div class="mt-2 text-sm text-red-700">
                    <ul role="list" class="list-disc space-y-1 pl-4">
                      <li v-for="(er, k) in formError.errors" :key="k">
                        {{ er[0] }}
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </div>
        <div v-else>
          <div>Connect you account for OAuth 2.0 on WhiteBIT</div>
        </div>
      </template>
    </div>
    <div class="mt-4">
      <button
        @click="onStartConnect"
        type="button"
        class="flex w-full items-center justify-center px-3 py-3 font-semibold m-yellow-bg rounded-full text-black"
        :class="[formProcessing ? 'opacity-50 pointer-events-none' : '']"
      >
        <template v-if="formProcessing">
          <ButtonSpinner />
        </template>
        {{ $t("connect_exchange") }}
      </button>
    </div>
  </div>
</template>

<script>
import LoadingBlock from "@/components/LoadingBlock.vue";
import ButtonSpinner from "@/components/ButtonSpinner.vue";
import {
  Listbox,
  ListboxButton,
  ListboxLabel,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/vue";
import {
  CheckIcon,
  ChevronUpDownIcon,
  InformationCircleIcon,
  XCircleIcon,
} from "@heroicons/vue/20/solid";
import ListIps from "@/components/elem/ListIps.vue";

export default {
  props: ["title", "desc"],
  components: {
    LoadingBlock,
    ButtonSpinner,
    Listbox,
    ListboxButton,
    ListboxLabel,
    ListboxOption,
    ListboxOptions,
    CheckIcon,
    ChevronUpDownIcon,
    InformationCircleIcon,
    XCircleIcon,
    ListIps
  },

  methods: {
    onChangeTab(tab) {
      this.activeTab = tab.key;
    },
    loadExchangeTypes() {
      this.loading = true;
      this.$api
        .get("/exchange-types")
        .then((result) => {
          this.exchanges = result.data.data;

          if (this.exchanges.length) {
            this.selectedExchange = this.exchanges[0];
          }

          this.loading = false;
        })
        .catch((e) => {
          this.error = e;
          this.loading = false;
        });
    },

    onStartConnect() {
      this.formProcessing = true;

      if (this.activeTab === 'oauth') {
        this.whitebitToRedirect();
      } else {
        this.connectExchangeAccount();
      }
    },

    whitebitToRedirect() {
      this.$api.post('/whitebit/auth/url').then((result) => {
        console.log(result.data);
        window.location.href = result.data.to_url;
      }).catch((e) => {
        console.log('Error', e);
        this.formProcessing = false;
      });
    },

    connectExchangeAccount() {
      if (this.selectedExchange) {
        this.form.type_exchange = this.selectedExchange.slug;
      }

      this.$api
        .post("/exchanges/new", this.form)
        .then((result) => {
          if (result.data.status === 1) {
            this.$emit("action-success", 1);
          } else {
            this.formError.message = "Error connect";
            this.formError.errors = {
              error_connect: [result.data.message],
            };
            this.formProcessing = false;
          }
        })
        .catch((e) => {
          console.log('Error', e.response.data);
          this.formError = e.response.data;
          this.formProcessing = false;
        });
    },
  },

  data() {
    return {
      loading: true,
      error: null,
      selectedExchange: null,
      allowedIPs: this.$settings.list_ips,
      exchanges: [],
      formError: {
        message: "",
        errors: null,
        status: 0,
      },
      form: {
        type_exchange: "whitebit",
        api_key: "",
        api_secret: "",
        api_passphrase: "",
      },
      formProcessing: false,

      activeTab: 'manual',
      tabs: [
        { name: 'OAuth', key: 'oauth' },
        { name: 'Manual', key: 'manual' },
      ],
    };
  },

  mounted() {
    this.loadExchangeTypes();
  },
};
</script>
